import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { DialogBase } from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useCallback } from "react";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import { handleAsyncOperation } from "@/shared";
import { useDeleteSlidesFolderMutation } from "@/shared/services";
import type { TSlidesFolder } from "@/shared/types";

const DIALOG_ID = "permanent-delete-folder-dialog";

const FolderPermanentDeleteDialog = ({
  folder,
}: {
  folder: TSlidesFolder;
}) => {
  const dispatch = useAppDispatch();
  const { mutateAsync: deleteFolder, isPending: isLoading } =
    useDeleteSlidesFolderMutation();

  const handleClose = useCallback(
    () => dispatch(closeDialog(DIALOG_ID)),
    [dispatch],
  );

  const handleSubmit = useCallback(async () => {
    if (isLoading) return;
    await handleAsyncOperation({
      operation: async () => {
        await deleteFolder(folder._id);
        toast.success("Folder deleted permanently");
        handleClose();
      },
      onError: () => toast.error("Failed to delete folder"),
    });
  }, [deleteFolder, folder._id, handleClose, isLoading]);

  return (
    <DialogBase>
      <DialogBase.Header title="Delete permanently" onClose={handleClose} />
      <DialogBase.Content className="tw-pt-4">
        <p className="tw-m-0 tw-text-[13px] tw-font-normal tw-text-blue">
          Are you sure that you want to permanently delete{" "}
          <span className="tw-font-semibold">“{folder.title}”</span> folder
          <br />
          with all of its subfolders and slides?
          <br />
          <br />
          This action cannot be undone.
        </p>
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          className={cn(isLoading && "tw-cursor-not-allowed")}
          variant="destructive"
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Deleting...
            </>
          ) : (
            "Yes, delete permanently"
          )}
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

FolderPermanentDeleteDialog.id = DIALOG_ID;
export { FolderPermanentDeleteDialog };
